import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import FranklyNetBanner from '../components/franklynet/FranklyNetBanner';
import FranklyNetResults from '../components/franklynet/FranklyNetResults';
import FranklyNetHome from '../components/franklynet/FranklyNetHome';
import FranklyNetViewer from '../components/franklynet/FranklyNetViewer';
import { isFrameDoc } from '../components/franklynet/frameLinks';
import { readQuery, resultsPath, docPath, isViewerPath, siteName } from '../components/franklynet/franklyNetHost';
import { askFrank, getFranklyNetLinks } from '../services/api';

function FranklyNet() {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { slug: articleSlug } = useParams();
    const query = readQuery(location, searchParams);
    const viewUrl = searchParams.get('url') || '';
    const rawDoc = searchParams.get('doc') || '';
    const viewDoc = isFrameDoc(rawDoc) ? rawDoc : '';
    const legacyArticleDoc = location.pathname.startsWith('/franklynet/article') && articleSlug
        ? docPath('article', articleSlug)
        : '';
    const doc = viewDoc || legacyArticleDoc;
    const page = Math.max(1, Number(searchParams.get('page')) || 1);
    const isViewer = Boolean(doc) || isViewerPath(location);

    const [answer, setAnswer] = useState(null);
    const [entity, setEntity] = useState(null);
    const [results, setResults] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, totalPages: 0, total: 0 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [recommended, setRecommended] = useState([]);

    useEffect(() => {
        document.title = query ? `${query} - ${siteName()}` : siteName();
    }, [query]);

    useEffect(() => {
        let active = true;

        getFranklyNetLinks()
            .then(res => { if (active && res.success) setRecommended(res.links || []); })
            .catch(() => {});

        return () => { active = false; };
    }, []);

    const runSearch = useCallback(async () => {
        if (!query || isViewer) return;

        setLoading(true);
        setError(null);

        const res = await askFrank({ q: query, page });

        if (res.success) {
            if (res.answer) setAnswer(res.answer);
            setEntity(res.entity || null);
            setResults(res.results || []);
            setSuggestions(res.suggestions || []);
            setPagination(res.pagination || { page: 1, totalPages: 0, total: 0 });
        } else {
            setError(res.message || 'Frank could not reach his files just then.');
            setEntity(null);
            setResults([]);
            setSuggestions([]);
            setPagination({ page: 1, totalPages: 0, total: 0 });
        }

        setLoading(false);
    }, [query, page, isViewer]);

    useEffect(() => {
        runSearch();
    }, [runSearch]);

    const handleSearch = (nextQuery) => {
        navigate(resultsPath(nextQuery));
    };

    const handlePage = (nextPage) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', String(nextPage));
        setSearchParams(params);
        window.scrollTo(0, 0);
    };

    if (isViewer && (doc || viewUrl)) {
        return (
            <div className="flex h-[100dvh] flex-col overflow-hidden bg-coal_dark">
                <FranklyNetBanner query={query} recommended={recommended} onSearch={handleSearch} />

                <div className="min-h-0 flex-1">
                    <FranklyNetViewer url={viewUrl} doc={doc} query={query} />
                </div>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen flex-col bg-coal_dark">
            <FranklyNetBanner query={query} recommended={recommended} onSearch={handleSearch} />

            <div className="w-full flex-1 bg-white/5">
                <div className="mx-auto w-full max-w-[1400px] px-4 py-5">
                    {query ? (
                        <FranklyNetResults
                            query={query}
                            answer={answer}
                            entity={entity}
                            results={results}
                            suggestions={suggestions}
                            pagination={pagination}
                            loading={loading}
                            error={error}
                            onPage={handlePage}
                            onSearch={handleSearch}
                        />
                    ) : (
                        <FranklyNetHome onSearch={handleSearch} />
                    )}
                </div>
            </div>

            <div className="bg-offwhite pt-4">
                <Footer />
            </div>
        </div>
    );
}

export default FranklyNet;
